// Libs
import { useEffect } from 'react';
import { useSession, signIn } from 'next-auth/react';
// App
import { hasAtLeastOneOfAuthorities } from 'app-shared/lib/api/user-access/auth/auth.utils';
import { hasValidAccessToken } from 'app-shared/lib/api/user-access/auth/access-token.utils';
import { AuthConfig } from './auth.interfaces';

import { FullpageSpinner } from 'app-shared/lib/components/feedback/fullpage-spinner';

/**
 * Wrapper for all routes containing authentication
 * There is two types of authenticated routes :
 * - Private routes (restrictedAccess is true) : the user needs to be logged in with right role
 * - Public routes : the user can be logged in or not
 */
const Auth = ({ children, config }: { children: JSX.Element; config: AuthConfig }): JSX.Element => {
  const { data: session, status } = useSession();
  const isSessionLoading = status == 'loading';
  const isAuthorized = hasAtLeastOneOfAuthorities(session, config.authorizedAuthorities);
  const isAuthenticated = hasValidAccessToken(session) && isAuthorized;
  const shouldSignIn = config.restrictedAccess && !isSessionLoading && !isAuthenticated;

  useEffect(() => {
    if (!shouldSignIn) return;

    signIn();
  }, [shouldSignIn]);

  if (!config.restrictedAccess) return children;

  if (isSessionLoading || shouldSignIn) {
    return <FullpageSpinner />;
  }

  return children;
};

export default Auth;
